import { create } from 'zustand';
import type { Pedido, StatusBackend } from './types';
import { listarPedidos, patchStatus, deletarPedido, atualizarPedido, type AtualizarPedidoInput } from './api';

/** YYYY-MM-DD ou 'all' para listar todos */
export type FiltroData = string;

export function dataHojeISO(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

interface PedidosState {
  pedidos: Record<number, Pedido>;
  carregando: boolean;
  erro: string | null;
  data: FiltroData;
  carregar: (data?: FiltroData) => Promise<void>;
  setData: (data: FiltroData) => Promise<void>;
  upsert: (p: Pedido) => void;
  remover: (id: number) => void;
  mudarStatus: (id: number, status: StatusBackend) => Promise<void>;
  excluir: (id: number) => Promise<void>;
  atualizar: (id: number, input: AtualizarPedidoInput) => Promise<Pedido>;
  selecionado: number | null;
  selecionar: (id: number | null) => void;
}

// monotônico: descarta respostas obsoletas (rapid prev/next clicks)
let _carregarSeq = 0;
const _inflight: Map<number, AbortController> = new Map();

export const usePedidosStore = create<PedidosState>((set, get) => ({
  pedidos: {},
  carregando: false,
  erro: null,
  data: dataHojeISO(),
  selecionado: null,
  carregar: async (data) => {
    const alvo = data ?? get().data;
    const seq = ++_carregarSeq;
    // aborta requisições anteriores pendentes
    for (const [k, ac] of _inflight) {
      if (k < seq) {
        ac.abort();
        _inflight.delete(k);
      }
    }
    const ac = new AbortController();
    _inflight.set(seq, ac);
    set({ carregando: true, erro: null, data: alvo, selecionado: null });
    try {
      const lista = await listarPedidos(alvo, ac.signal);
      // ignora se não é a resposta mais recente
      if (seq !== _carregarSeq) return;
      const map: Record<number, Pedido> = {};
      for (const p of lista) map[p.id] = p;
      set({ pedidos: map, carregando: false });
    } catch (err) {
      if ((err as Error).name === 'AbortError') return;
      if (seq === _carregarSeq) {
        set({ erro: (err as Error).message, carregando: false });
      }
    } finally {
      _inflight.delete(seq);
    }
  },
  setData: async (data) => {
    await get().carregar(data);
  },
  upsert: (p) =>
    set((s) => ({ pedidos: { ...s.pedidos, [p.id]: { ...s.pedidos[p.id], ...p } } })),
  remover: (id) =>
    set((s) => {
      const next = { ...s.pedidos };
      delete next[id];
      return {
        pedidos: next,
        selecionado: s.selecionado === id ? null : s.selecionado,
      };
    }),
  atualizar: async (id, input) => {
    const atualizado = await atualizarPedido(id, input);
    set((s) => ({ pedidos: { ...s.pedidos, [id]: atualizado } }));
    return atualizado;
  },
  excluir: async (id) => {
    const anterior = get().pedidos[id];
    // optimistic remove
    set((s) => {
      const next = { ...s.pedidos };
      delete next[id];
      return {
        pedidos: next,
        selecionado: s.selecionado === id ? null : s.selecionado,
      };
    });
    try {
      await deletarPedido(id);
    } catch (err) {
      // rollback
      if (anterior) set((s) => ({ pedidos: { ...s.pedidos, [id]: anterior } }));
      throw err;
    }
  },
  mudarStatus: async (id, status) => {
    const anterior = get().pedidos[id];
    // optimistic
    if (anterior) set((s) => ({ pedidos: { ...s.pedidos, [id]: { ...anterior, status } } }));
    try {
      const atualizado = await patchStatus(id, status);
      set((s) => {
        const nextPedidos = { ...s.pedidos, [id]: atualizado };
        // se card saiu do board (terminal/em_rota), fecha drawer associado
        const fora = ['em_rota', 'entregue', 'cancelado'].includes(atualizado.status);
        return {
          pedidos: nextPedidos,
          selecionado: fora && s.selecionado === id ? null : s.selecionado,
        };
      });
    } catch (err) {
      if (anterior) set((s) => ({ pedidos: { ...s.pedidos, [id]: anterior } }));
      throw err;
    }
  },
  selecionar: (id) => set({ selecionado: id }),
}));

export function ordenarPedidos(map: Record<number, Pedido>): Pedido[] {
  return Object.values(map).sort((a, b) => {
    if (a.criado_em < b.criado_em) return 1;
    if (a.criado_em > b.criado_em) return -1;
    return 0;
  });
}
